import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, MapPin, Package, RotateCcw, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead } from "@/components/common";
import { COUNTRIES, isUS, regionLabel, postalLabel } from "@/lib/countries";

const EMPTY_ADDR = { name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" };
const EMPTY_PKG = { weight: 1, length: 8, width: 6, height: 4 };

function AddressFields({ prefix, value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const us = isUS(value.country);
  const handleZip = async (e) => {
    if (!us) { onChange({ ...value, zip: e.target.value }); return; }  // free-form postal for international
    const zip = e.target.value.replace(/[^\d]/g, "").slice(0, 5);
    onChange({ ...value, zip });
    if (/^\d{5}$/.test(zip)) {
      try {
        const g = await api.get(`/geo/zip/${zip}`);
        if (g.city || g.state) onChange({ ...value, zip, city: g.city || value.city, state: g.state || value.state });
      } catch { /* silent — seller can type city/state manually */ }
    }
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
      <div style={{ gridColumn: "1 / -1" }}>
        <label className="ss-label">Country</label>
        <div className="ss-field">
          <select value={value.country || "US"} onChange={(e) => onChange({ ...value, country: e.target.value })} data-testid={`${prefix}-country`}
            style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 14, cursor: "pointer" }}>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label className="ss-label">Full name / business</label>
        <div className="ss-field"><input value={value.name} onChange={set("name")} placeholder="Amira's Attar House" data-testid={`${prefix}-name`} /></div>
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label className="ss-label">Street address</label>
        <div className="ss-field"><input value={value.street1} onChange={set("street1")} placeholder="123 Market St" data-testid={`${prefix}-street1`} /></div>
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label className="ss-label">Apt / suite (optional)</label>
        <div className="ss-field"><input value={value.street2} onChange={set("street2")} placeholder="Suite 200" data-testid={`${prefix}-street2`} /></div>
      </div>
      <div>
        <label className="ss-label">City</label>
        <div className="ss-field"><input value={value.city} onChange={set("city")} placeholder="Austin" data-testid={`${prefix}-city`} /></div>
      </div>
      <div>
        <label className="ss-label">{regionLabel(value.country)}{us ? "" : " (optional)"}</label>
        <div className="ss-field"><input value={value.state} onChange={set("state")} placeholder={us ? "TX" : "e.g. Ontario"} data-testid={`${prefix}-state`} /></div>
      </div>
      <div>
        <label className="ss-label">{postalLabel(value.country)}</label>
        <div className="ss-field"><input value={value.zip} onChange={handleZip} placeholder={us ? "78701" : "e.g. SW1A 1AA"} data-testid={`${prefix}-zip`} inputMode={us ? "numeric" : "text"} /></div>
      </div>
      <div>
        <label className="ss-label">Phone</label>
        <div className="ss-field"><input value={value.phone} onChange={set("phone")} placeholder="+1 555 010 0100" data-testid={`${prefix}-phone`} /></div>
      </div>
    </div>
  );
}

export default function ShippingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [origin, setOrigin] = useState(EMPTY_ADDR);
  const [ret, setRet] = useState(EMPTY_ADDR);
  const [sameReturn, setSameReturn] = useState(true);
  const [pkg, setPkg] = useState(EMPTY_PKG);
  const [flatFee, setFlatFee] = useState("");
  const [intlFlatFee, setIntlFlatFee] = useState("");

  useEffect(() => {
    api.get("/shipping/profile").then((d) => {
      const p = d.profile;
      if (p) {
        setOrigin({ ...EMPTY_ADDR, ...p.origin });
        if (p.return_address && JSON.stringify(p.return_address) !== JSON.stringify(p.origin)) {
          setRet({ ...EMPTY_ADDR, ...p.return_address });
          setSameReturn(false);
        }
        if (p.default_package) setPkg({ ...EMPTY_PKG, ...p.default_package });
        if (p.default_flat_fee) setFlatFee(p.default_flat_fee);
        if (p.default_intl_flat_fee) setIntlFlatFee(p.default_intl_flat_fee);
      } else if (d.fallback) {
        setOrigin({ ...EMPTY_ADDR, ...d.fallback });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const req = isUS(origin.country) ? ["name", "street1", "city", "state", "zip"] : ["name", "street1", "city", "zip"];
    for (const f of req) {
      if (!origin[f]?.trim()) return toast.error("Please complete your origin address");
    }
    setSaving(true);
    try {
      const body = {
        origin,
        return_address: sameReturn ? origin : ret,
        default_package: {
          weight: Number(pkg.weight) || 1, length: Number(pkg.length) || 8,
          width: Number(pkg.width) || 6, height: Number(pkg.height) || 4,
        },
        default_flat_fee: Number(flatFee) > 0 ? Number(flatFee) : null,
        default_intl_flat_fee: Number(intlFlatFee) > 0 ? Number(intlFlatFee) : null,
      };
      await api.put("/shipping/profile", body);
      toast.success("Shipping settings saved");
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  const numField = (label, k) => (
    <div>
      <label className="ss-label">{label}</label>
      <div className="ss-field" style={{ height: 44 }}>
        <input type="number" min="0" step="0.1" value={pkg[k]} onChange={(e) => setPkg({ ...pkg, [k]: e.target.value })} data-testid={`pkg-${k}`} />
      </div>
    </div>
  );

  return (
    <div className="fade-up" style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHead title="Shipping Settings" sub="Set where your packages ship from, where returns go, and your default box size." />
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div className="ss-card" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, marginBottom: 16 }}>
            <div className="stat-ic"><MapPin size={18} /></div> Ship-from (origin) address
          </div>
          <AddressFields prefix="origin" value={origin} onChange={setOrigin} />
        </div>

        <div className="ss-card" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800 }}>
              <div className="stat-ic"><RotateCcw size={18} /></div> Return address
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={sameReturn} onChange={(e) => setSameReturn(e.target.checked)} data-testid="same-return-toggle" />
              Same as origin
            </label>
          </div>
          {!sameReturn && <AddressFields prefix="return" value={ret} onChange={setRet} />}
          {sameReturn && <div className="ss-muted" style={{ fontSize: 14 }}>Returns will be sent to your origin address.</div>}
        </div>

        <div className="ss-card" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, marginBottom: 16 }}>
            <div className="stat-ic"><Package size={18} /></div> Default package
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            {numField("Weight (lb)", "weight")}
            {numField("Length (in)", "length")}
            {numField("Width (in)", "width")}
            {numField("Height (in)", "height")}
          </div>
          <div className="ss-muted" style={{ fontSize: 13, marginTop: 10 }}>Used to calculate rates for items without their own package dimensions.</div>
        </div>

        <div className="ss-card" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, marginBottom: 8 }}>
            <div className="stat-ic"><Truck size={18} /></div> Default flat shipping fee
          </div>
          <div className="ss-muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Charge buyers one flat rate for every item that doesn't set its own (a $2 platform fee is added).
            Leave blank to use live carrier rates. Per-item settings always override this.
          </div>
          <div className="ss-field" style={{ height: 44, maxWidth: 220 }}>
            <span className="ss-muted" style={{ paddingLeft: 4 }}>$</span>
            <input type="number" min="0" step="0.01" placeholder="e.g. 5.99" value={flatFee} onChange={(e) => setFlatFee(e.target.value)} data-testid="default-flat-fee" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, margin: "18px 0 8px" }}>
            <div className="stat-ic"><Globe size={18} /></div> International flat shipping fee
          </div>
          <div className="ss-muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Charge overseas buyers (any non-US address) one fair flat rate. You ship internationally with your own
            carrier and add the tracking manually. Leave blank to fall back to your standard flat fee.
          </div>
          <div className="ss-field" style={{ height: 44, maxWidth: 220 }}>
            <span className="ss-muted" style={{ paddingLeft: 4 }}>$</span>
            <input type="number" min="0" step="0.01" placeholder="e.g. 24.99" value={intlFlatFee} onChange={(e) => setIntlFlatFee(e.target.value)} data-testid="default-intl-flat-fee" />
          </div>
        </div>

        <button className="ss-btn ss-btn-primary ss-btn-block" disabled={saving} data-testid="save-shipping-settings">
          <Truck size={16} /> {saving ? "Saving…" : "Save shipping settings"}
        </button>
      </form>
    </div>
  );
}
